const INVALID_STATUS_FRAGMENTS = [
  "refunded",
  "refund success",
  "processing refund",
  "return required",
  "return in transit",
  "refund awaiting seller confirmation",
  "cancelled",
  "canceled",
];

export function round2(value) {
  return Number((Number(value) || 0).toFixed(2));
}

export function isValidOrderForRevenue(order) {
  const status = String(order?.status || "").toLowerCase();
  return !INVALID_STATUS_FRAGMENTS.some((fragment) => status.includes(fragment));
}

export function isOrderPaymentCaptured(order) {
  const status = String(order?.paymentInfo?.status || "").toLowerCase().trim();
  if (status === "succeeded" || status === "paid" || status === "processing") return true;
  const type = String(order?.paymentInfo?.type || "").toLowerCase().trim();
  if (type === "wallet") return true;
  return false;
}

export function getOrderPaymentMethod(order) {
  const type = String(order?.paymentInfo?.type || "").toLowerCase().trim();
  const walletUsed = Number(order?.walletAmountUsed || 0) > 0;
  if (walletUsed && type === "stripe") return "mixed";
  if (type === "wallet") return "wallet";
  if (type === "stripe") return "stripe";
  if (type === "klarna") return "klarna";
  return type || "other";
}

export function computeOrderVaBreakdown(order, feeSettings = {}) {
  const platformFees = feeSettings.platformFees || { enabled: true, percentage: 5 };
  const stripeFees = feeSettings.stripeFees || { enabled: true, percentage: 2.9, fixedFee: 0.3 };

  const subtotal = round2(order?.subTotalPrice ?? 0);
  const buyerProtectionFee = round2(order?.buyerProtectionFee ?? 0);
  const featuredItemFee = round2(order?.featuredItemFee ?? 0);
  const platformFeeAmount = platformFees.enabled
    ? round2((subtotal * (Number(platformFees.percentage) || 0)) / 100)
    : 0;

  const orderTotal = round2(order?.totalPrice ?? 0);
  const paymentMethod = getOrderPaymentMethod(order);
  const chargesViaStripe =
    paymentMethod === "stripe" || paymentMethod === "mixed" || paymentMethod === "klarna";

  const stripeFeeAmount =
    chargesViaStripe && stripeFees.enabled
      ? round2(
          (orderTotal * (Number(stripeFees.percentage) || 0)) / 100 +
            (Number(stripeFees.fixedFee) || 0)
        )
      : 0;

  const vaTotal = round2(buyerProtectionFee + featuredItemFee + platformFeeAmount);
  const vaOrderEarnings = round2(buyerProtectionFee + platformFeeAmount - stripeFeeAmount);

  const paid = isOrderPaymentCaptured(order);
  const released = order?.payoutReleased === true;
  const inEscrow = paid && !released;

  return {
    subtotal,
    buyerProtectionFee,
    featuredItemFee,
    platformFeeAmount,
    stripeFeeAmount,
    vaTotal,
    vaOrderEarnings,
    paymentMethod,
    inEscrow,
    released,
    paid,
  };
}

export function summarizeOrdersVaRevenue(orders = [], feeSettings = {}) {
  const summary = {
    orderCount: 0,
    buyerProtectionFees: 0,
    sellerPlatformFees: 0,
    featuredListingFees: 0,
    vaTotalGross: 0,
    stripeFeesEstimated: 0,
    vaNetEarnings: 0,
    vaReleased: 0,
    vaPendingInEscrow: 0,
    escrowOrderValueStripe: 0,
    escrowOrderValueWallet: 0,
    escrowOrderValueMixed: 0,
    escrowOrderValueTotal: 0,
    escrowOrderCount: 0,
    byPaymentMethod: {
      stripe: { orders: 0, vaEarnings: 0 },
      wallet: { orders: 0, vaEarnings: 0 },
      mixed: { orders: 0, vaEarnings: 0 },
      other: { orders: 0, vaEarnings: 0 },
    },
  };

  for (const order of orders) {
    if (!order || !isValidOrderForRevenue(order) || !isOrderPaymentCaptured(order)) continue;

    const breakdown = computeOrderVaBreakdown(order, feeSettings);
    summary.orderCount += 1;
    summary.buyerProtectionFees = round2(summary.buyerProtectionFees + breakdown.buyerProtectionFee);
    summary.sellerPlatformFees = round2(summary.sellerPlatformFees + breakdown.platformFeeAmount);
    summary.featuredListingFees = round2(summary.featuredListingFees + breakdown.featuredItemFee);
    summary.vaTotalGross = round2(summary.vaTotalGross + breakdown.vaTotal);
    summary.stripeFeesEstimated = round2(summary.stripeFeesEstimated + breakdown.stripeFeeAmount);
    summary.vaNetEarnings = round2(summary.vaNetEarnings + breakdown.vaOrderEarnings);

    if (breakdown.released) {
      summary.vaReleased = round2(summary.vaReleased + breakdown.vaOrderEarnings);
    } else if (breakdown.inEscrow) {
      summary.vaPendingInEscrow = round2(summary.vaPendingInEscrow + breakdown.vaOrderEarnings);
    }

    const methodKey = ["stripe", "wallet", "mixed"].includes(breakdown.paymentMethod)
      ? breakdown.paymentMethod
      : "other";
    summary.byPaymentMethod[methodKey].orders += 1;
    summary.byPaymentMethod[methodKey].vaEarnings = round2(
      summary.byPaymentMethod[methodKey].vaEarnings + breakdown.vaOrderEarnings
    );

    if (breakdown.inEscrow) {
      const orderValue = round2(order?.totalPrice ?? 0);
      summary.escrowOrderCount += 1;
      summary.escrowOrderValueTotal = round2(summary.escrowOrderValueTotal + orderValue);
      if (breakdown.paymentMethod === "wallet") {
        summary.escrowOrderValueWallet = round2(summary.escrowOrderValueWallet + orderValue);
      } else if (breakdown.paymentMethod === "mixed") {
        summary.escrowOrderValueMixed = round2(summary.escrowOrderValueMixed + orderValue);
      } else {
        summary.escrowOrderValueStripe = round2(summary.escrowOrderValueStripe + orderValue);
      }
    }
  }

  return summary;
}
